import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { mdiPlus } from '@mdi/js';
import { useNavigate } from 'react-router';

import { IAppPermissionsCertificate, IFeature } from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { ActionButton } from 'common/components/ActionButton';
import { PageTitle } from 'common/components/PageTitle';
import { Icon } from 'common/components/Icon';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';
import {
    UpgradeContent,
    CertificatesManagementTable,
} from 'certificate/components';
import { BasePage } from 'hydra/pages/BasePage';
import { getUrl } from 'route/utils/getUrl';
import { useAppPermissions } from 'user/hooks/usePermissions';

export const CertificatesManagementPage = () => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const [loadingCertificates, setLoadingCertificates] = useState(false);
    const {
        canUse: canUseCertificate,
        userCanSeeUpgrade,
        loading: loadingFeature,
    } = useFeature(IFeature.Certificate);
    const { canRead = false }: IAppPermissionsCertificate['management'] =
        useAppPermissions('certificate', 'management');

    const showUpgradePage = !canUseCertificate && userCanSeeUpgrade;

    // User cannot use certificate module and cannot see upgrade page
    if (
        (!loadingFeature && !canUseCertificate && !userCanSeeUpgrade) ||
        (!canRead && !userCanSeeUpgrade)
    ) {
        return <FourOFourPage />;
    }

    return (
        <BasePage contentContainerMaxWidth="lg">
            <PageTitle>
                {translate('certificatesManagementPage.browserTitle')}
            </PageTitle>

            {!showUpgradePage ? (
                <>
                    <Box sx={{ pt: 4 }}>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Grid size={{ sm: 5, md: 'grow' }}>
                                <Typography variant="h1">
                                    {translate(
                                        'certificatesManagementPage.pageTitle'
                                    )}
                                </Typography>
                            </Grid>

                            <Grid
                                size={{ sm: 7, md: 'auto' }}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    justifyContent: 'flex-end',
                                    flexShrink: 0,
                                }}
                            >
                                <ActionButton
                                    outlined
                                    disabled={loadingCertificates}
                                    variant="extended"
                                    onClick={() => {
                                        navigate(getUrl('CERTIFICATES_CREATE'));
                                    }}
                                >
                                    <Box>
                                        <Icon path={mdiPlus} size="2.4rem" />
                                    </Box>

                                    {translate(
                                        'certificatesManagementPage.newCertificate'
                                    )}
                                </ActionButton>

                                <ActionButton
                                    outlined
                                    disabled={loadingCertificates}
                                    variant="extended"
                                    onClick={() => {
                                        navigate(
                                            getUrl(
                                                'EXTERNAL_CERTIFICATE_CREATE'
                                            )
                                        );
                                    }}
                                >
                                    <Box>
                                        <Icon path={mdiPlus} size="2.4rem" />
                                    </Box>

                                    {translate(
                                        'certificatesManagementPage.newExternalCertificate'
                                    )}
                                </ActionButton>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ py: 4 }}>
                        <CertificatesManagementTable
                            onLoading={setLoadingCertificates}
                        />
                    </Box>
                </>
            ) : (
                <UpgradeContent />
            )}
        </BasePage>
    );
};
